import { getTheme } from '@fluentui/react';
import {
  AreaChart,
  DataVizPalette,
  DonutChart,
  getColorFromToken,
  GroupedVerticalBarChart,
  IChartProps,
  LineChart,
  VerticalBarChart,
  VerticalStackedBarChart,
} from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalTextStylesBold } from 'globalStyles';
import { ChartDefinition, ChartSeries, hasChartValues, parseChartBlockPayload } from 'models/copilot/copilotChartBlock';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// fits the panel's block width with the axis labels and a legend below
const chartHeight = 260;

// the series after the first, in the data visualization palette of the charting package (light and dark)
const palette = [
  DataVizPalette.color2,
  DataVizPalette.color3,
  DataVizPalette.color4,
  DataVizPalette.color5,
  DataVizPalette.color6,
  DataVizPalette.color7,
  DataVizPalette.color8,
  DataVizPalette.color9,
  DataVizPalette.color10,
  DataVizPalette.color11,
  DataVizPalette.color12,
];

const isoDate = /^\d{4}-\d{2}-\d{2}/;

// line and area charts of v5 take a number or a date on the x axis and format only date ticks: labels that are not
// dates are placed on consecutive days from this base and the tick formatter prints the label back
const labelAxisBase = Date.UTC(2000, 0, 1);
const dayMs = 24 * 60 * 60 * 1000;

interface ICopilotChartBlockProps {
  block: CopilotConversationContentBlock;
}

/**
 * The Chart content block of a Copilot answer, drawn from its stored definition on @fluentui/react-charting in the
 * conventions of the KPI charts: the first series in the theme's primary color, the others in the data visualization
 * palette, no legend for one series, a gap for a label without a value (never 0), a no-data line instead of an empty
 * frame. Mapped from SkillControl's IPChart (Fluent v9 charts); no shared code.
 */
export const CopilotChartBlock = (props: ICopilotChartBlockProps) => {
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const definition = useMemo(() => parseChartBlockPayload(props.block), [props.block]);

  if (!hasChartValues(definition)) {
    return <IPText variant="small">{t('copilot:Chat.Chart.NoData')}</IPText>;
  }

  return (
    <IPStack tokens={{ childrenGap: 4 }}>
      {definition.title && <IPText styles={globalTextStylesBold}>{definition.title}</IPText>}
      <IPStack.Item>{renderChart(definition, appContext.useDarkMode)}</IPStack.Item>
    </IPStack>
  );
};

const renderChart = (definition: ChartDefinition, isDark: boolean): JSX.Element => {
  const primary = getTheme().palette.themePrimary;
  const colorOf = (index: number): string =>
    index === 0 ? primary : getColorFromToken(palette[(index - 1) % palette.length], isDark);
  const isPercent = definition.options?.valueFormat === 'percent';
  const formatValue = (value: number): string => (isPercent ? `${value.toLocaleString()} %` : value.toLocaleString());
  const series = definition.series.filter((item) => item.points.some((point) => point.value !== null));
  const isSingle = series.length === 1;
  // every label of every series in the order it first appears: a series need not have every label
  const labels = Array.from(new Set(series.flatMap((item) => item.points.map((point) => point.label))));
  const valueAt = (item: ChartSeries, label: string): number | null =>
    item.points.find((point) => point.label === label)?.value ?? null;
  const axisProps = {
    xAxisTitle: definition.options?.xTitle,
    yAxisTitle: definition.options?.yTitle,
    yAxisTickFormat: isPercent ? (value: number) => `${value} %` : undefined,
    height: chartHeight,
  };

  switch (definition.kind) {
    case 'donut':
      return (
        <DonutChart
          data={{
            chartData: series[0].points
              .filter((point) => point.value !== null)
              .map((point, index) => ({
                legend: point.label,
                data: point.value ?? 0,
                color: colorOf(index),
                yAxisCalloutData: formatValue(point.value ?? 0),
              })),
          }}
          innerRadius={55}
          height={chartHeight}
          enabledLegendsWrapLines={true}
        />
      );

    case 'stackedBar':
      return (
        <VerticalStackedBarChart
          data={labels.map((label) => ({
            xAxisPoint: label,
            chartData: series.flatMap((item, seriesIndex) => {
              const value = valueAt(item, label);

              return value === null
                ? []
                : [
                    {
                      legend: item.name,
                      data: value,
                      color: colorOf(seriesIndex),
                      yAxisCalloutData: formatValue(value),
                    },
                  ];
            }),
          }))}
          hideLegend={isSingle}
          wrapXAxisLables={true}
          {...axisProps}
        />
      );

    case 'line':
    case 'area': {
      const isDates = labels.every((label) => isoDate.test(label) && !isNaN(Date.parse(label)));
      const xOf = (label: string): Date =>
        isDates ? new Date(label) : new Date(labelAxisBase + labels.indexOf(label) * dayMs);
      const data: IChartProps = {
        lineChartData: series.map((item, seriesIndex) => ({
          legend: item.name,
          color: colorOf(seriesIndex),
          data: item.points
            .filter((point) => point.value !== null)
            .map((point) => ({
              x: xOf(point.label),
              y: point.value ?? 0,
              xAxisCalloutData: point.label,
              yAxisCalloutData: formatValue(point.value ?? 0),
            })),
        })),
      };
      const labelTicks = isDates
        ? {}
        : {
            tickValues: labels.map((label) => xOf(label)),
            customDateTimeFormatter: (date: Date) => labels[Math.round((date.getTime() - labelAxisBase) / dayMs)] ?? '',
          };

      return definition.kind === 'line' ? (
        <LineChart data={data} hideLegend={isSingle} wrapXAxisLables={true} {...labelTicks} {...axisProps} />
      ) : (
        <AreaChart data={data} hideLegend={isSingle} wrapXAxisLables={true} {...labelTicks} {...axisProps} />
      );
    }

    default:
      return isSingle ? (
        <VerticalBarChart
          data={series[0].points
            .filter((point) => point.value !== null)
            .map((point) => ({
              x: point.label,
              y: point.value ?? 0,
              legend: point.label,
              color: primary,
              yAxisCalloutData: formatValue(point.value ?? 0),
            }))}
          hideLegend={true}
          wrapXAxisLables={true}
          roundCorners={true}
          {...axisProps}
        />
      ) : (
        <GroupedVerticalBarChart
          data={labels.map((label) => ({
            name: label,
            series: series.flatMap((item, seriesIndex) => {
              const value = valueAt(item, label);

              return value === null
                ? []
                : [
                    {
                      key: `${seriesIndex}`,
                      legend: item.name,
                      data: value,
                      color: colorOf(seriesIndex),
                      yAxisCalloutData: formatValue(value),
                    },
                  ];
            }),
          }))}
          wrapXAxisLables={true}
          {...axisProps}
        />
      );
  }
};

export default CopilotChartBlock;
